import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Box,
  TextField,
  Button,
  Typography,
  Snackbar,
  Alert,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import BaseURL from "../../api/AppURL";
import { useParams, useNavigate } from "react-router-dom";

const groupOptions = [
  { id: "account", name: "Accounts" },
  { id: "dashboard", name: "Dashboard" },
  { id: "sale", name: "Sales" },
  { id: "hrm", name: "HRM" },
  { id: "manufacture", name: "Manufacture" },
  { id: "product", name: "Product" },
  { id: "authorise", name: "Authorise" },
  { id: "brand", name: "Brand" },
  { id: "category", name: "Category" },
  { id: "subcategory", name: "Subcategory" },
  { id: "slider", name: "Slider" },
  { id: "ads", name: "Ads" },
  { id: "coupon", name: "Coupon" },
  { id: "area", name: "Area" },
  { id: "vendor", name: "Vendor" },
  { id: "order", name: "Order" },
  { id: "return", name: "Return" },
  { id: "report", name: "Report" },
  { id: "user", name: "User Management" },
  { id: "review", name: "Review" },
  { id: "setting", name: "Setting" },
  { id: "blog", name: "Blog" },
  { id: "role", name: "Role" },
  { id: "admin", name: "Admin" },
  { id: "stock", name: "Stock" },
  { id: "website", name: "Website" },
  { id: "erp", name: "ERP" },
  { id: "areamanager", name: "Area Manager" },
  { id: "producttest", name: "Product Test" },
  { id: "permission", name: "Permission" },
  { id: "adminPermission", name: "AdminPermission" },
];

export default function EditPermission() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [values, setValues] = useState({
    username: "", // permission name
    role: null, // group_name
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((s) => ({ ...s, open: false }));
  };

  const handleChange = (field) => (e) => {
    setValues((v) => ({ ...v, [field]: e.target.value }));
    setErrors((err) => ({ ...err, [field]: null }));
  };

  const validate = () => {
    const err = {};
    if (!values.username.trim()) err.username = "Permission Name is required";
    if (!values.role) err.role = "Group Name is required";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  useEffect(() => {
    const fetchData = async () => {
      setFetching(true);
      try {
        const token = localStorage.getItem("auth_token");
        const res = await axios.get(
          `${BaseURL}/edit/permission/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );

        if (res.data && res.data.status === "success") {
          const perm = res.data.permission;
          setValues({
            username: perm.name || "",
            role: perm.group_name || null,
          });
        } else {
          setSnackbar({
            open: true,
            severity: "error",
            message: "Failed to load permission data",
          });
        }
      } catch (err) {
        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to load permission data",
        });
      } finally {
        setFetching(false);
      }
    };

    if (id) fetchData();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Fix validation errors",
      });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const postData = {
        name: values.username,
        group_name: values.role,
      };

      const res = await axios.put(
        `${BaseURL}/update/permission/${id}`, // নিশ্চিত হও তোমার API PUT route এর URL এটা
        postData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      setSnackbar({
        open: true,
        severity: "success",
        message: res.data.message || "Permission updated",
      });

      setTimeout(() => {
        navigate("/admin/all_permission");
      }, 1200);
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to update permission";
      setSnackbar({ open: true, severity: "error", message: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h6" gutterBottom>
          Edit Permission
        </Typography>
        {fetching ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            <TextField
              label="Permission Name"
              value={values.username}
              onChange={handleChange("username")}
              required
              error={!!errors.username}
              helperText={errors.username}
            />

            <Autocomplete
              options={groupOptions}
              getOptionLabel={(option) => option.name}
              value={groupOptions.find((r) => r.id === values.role) || null}
              onChange={(_, newValue) =>
                setValues((v) => ({
                  ...v,
                  role: newValue ? newValue.id : null,
                }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Group Name"
                  required
                  error={!!errors.role}
                  helperText={errors.role}
                />
              )}
            />

            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? "Updating..." : "Save Changes"}
            </Button>
          </Box>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
